import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, MapPinned, Receipt, Navigation, Car } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";

const CITIES = ["San Marcos", "Kyle", "Austin", "New Braunfels"];
const STEPS = [
  { icon: MapPinned, tone: "lime", title: "Pick your spots", text: "Choose from the Scoopz directory, search any address, or use your GPS." },
  { icon: Receipt, tone: "cyan", title: "See the fare first", text: "Route, distance, time and a full fare breakdown before you request." },
  { icon: Navigation, tone: "pink", title: "Track every step", text: "Get notified when your driver accepts, arrives and drops you off." },
];
const TONE = {
  lime: "text-neon-lime border-neon-lime/40 hover:shadow-[0_0_30px_hsl(var(--neon-lime)/0.25)]",
  cyan: "text-neon-cyan border-neon-cyan/40 hover:shadow-[0_0_30px_hsl(var(--neon-cyan)/0.25)]",
  pink: "text-neon-pink border-neon-pink/40 hover:shadow-[0_0_30px_hsl(var(--neon-pink)/0.25)]",
};

function Ctas() {
  const { user } = useAuth();
  if (user) {
    return (
      <Button asChild className="h-12 rounded-full px-7 text-base hover:-translate-y-0.5" data-testid="hero-open-app-button">
        <Link to={homeFor(user)}>Open Scoopz <ArrowRight className="ml-1 h-4 w-4" /></Link>
      </Button>
    );
  }
  return (
    <>
      <Button asChild className="h-12 rounded-full px-7 text-base hover:-translate-y-0.5" data-testid="hero-ride-button">
        <Link to="/register">Book a ride <ArrowRight className="ml-1 h-4 w-4" /></Link>
      </Button>
      <Button asChild variant="outline" className="h-12 rounded-full bg-transparent px-7 text-base hover:-translate-y-0.5" data-testid="hero-drive-button">
        <Link to="/register?role=driver"><Car className="mr-1 h-4 w-4" /> Drive with Scoopz</Link>
      </Button>
    </>
  );
}

function Hero() {
  return (
    <section className="px-4 pt-2 md:px-10">
      <div className="fade-up overflow-hidden rounded-[1.75rem] neon-border">
        <img src="/brand/banner.webp" srcSet="/brand/banner-1000.webp 1000w, /brand/banner.webp 2000w" sizes="100vw"
          alt="Scoopz Private Rides — Private rides. Your time. Your way." className="block h-auto w-full" data-testid="hero-banner" />
      </div>
      <div className="grid gap-10 px-2 pb-16 pt-12 md:grid-cols-[1.3fr_1fr] md:px-4">
        <div className="fade-up" style={{ animationDelay: "100ms" }}>
          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Private rides. <span className="neon-text-lime">Your time.</span> <span className="neon-text-pink">Your way.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
            Scoopz connects riders with local drivers across San Marcos, Kyle, Austin and New Braunfels — from campus to the outlets to the airport.
          </p>
          <div className="mt-8 flex flex-wrap gap-3"><Ctas /></div>
        </div>
        <div className="fade-up flex flex-col justify-center rounded-3xl border border-white/10 bg-card p-7" style={{ animationDelay: "180ms" }}>
          <p className="font-brush text-3xl leading-tight">
            <span className="neon-text-cyan">Same people.</span><br /><span className="neon-text-lime">More places.</span>
          </p>
          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">Serving</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {CITIES.map((c) => (
              <span key={c} className="rounded-full border border-neon-violet/50 bg-neon-violet/10 px-3 py-1 text-xs font-semibold text-white">{c}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="px-6 py-14 md:px-14">
      <h2 className="text-base font-semibold uppercase tracking-[0.3em] text-muted-foreground md:text-lg">How it works</h2>
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {STEPS.map((s, i) => (
          <div key={s.title} className={`fade-up rounded-3xl border bg-card p-7 transition-shadow ${TONE[s.tone]}`} style={{ animationDelay: `${i * 90}ms` }}>
            <s.icon className="h-7 w-7" />
            <p className="mt-5 font-display text-xl font-semibold tracking-tight text-white">{s.title}</p>
            <p className="mt-2 text-sm text-muted-foreground">{s.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function DirectoryStrip() {
  const { data } = useQuery({ queryKey: ["landingLocations"], queryFn: () => api.get("/locations", { params: { limit: 12 } }).then((r) => r.data) });
  return (
    <section className="grain neon-border relative mx-4 overflow-hidden rounded-[2rem] bg-card px-6 py-14 md:mx-10 md:px-14">
      <div className="relative z-10 grid gap-8 md:grid-cols-[1fr_1.4fr]">
        <div>
          <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">The places you actually go.</h2>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">
            A curated directory of local campuses, stores, hotels and landmarks makes booking a one-tap job.
          </p>
        </div>
        <div className="flex flex-wrap content-start gap-2" data-testid="landing-directory-list">
          {data?.map((l) => (
            <span key={l.id} className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-white/90 transition-colors hover:border-neon-cyan/60">
              {l.name} <span className="text-white/45">· {l.city}</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <header className="flex items-center justify-between px-6 py-5 md:px-12">
        <Brand />
        {!user && (
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" className="rounded-full" data-testid="nav-login-button"><Link to="/login">Log in</Link></Button>
            <Button asChild className="rounded-full" data-testid="nav-signup-button"><Link to="/register">Sign up</Link></Button>
          </div>
        )}
      </header>
      <Hero />
      <HowItWorks />
      <DirectoryStrip />
      <footer className="flex flex-wrap items-center justify-between gap-4 px-6 py-10 text-sm text-muted-foreground md:px-14">
        <span>© {new Date().getFullYear()} Scoopz Private Rides · Map data © OpenStreetMap contributors</span>
        <span className="font-brush text-lg"><span className="neon-text-lime">Ride smart.</span> <span className="neon-text-pink">Ride Scoopz.</span></span>
      </footer>
    </div>
  );
}
